// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useCallback, useEffect, useRef, useState } from 'react';
import { ActivityIndicator, Alert, Platform, Pressable, StyleSheet } from 'react-native';

import { Text } from '@/components/Themed';
import { useI18n } from '@/i18n';
import appColors, { alpha } from '@/theme/colors';

import { newPhotoId } from '../newPhotoId';
import { fromWebFiles, pickFromCamera, type PickOutcome } from '../pickPhotos';
import {
  enqueuePhotoUpload,
  setPhotoUploadAuthToken,
  subscribeToPhotoUploads,
} from '../uploadQueue';

type HolePhotoButtonProps = {
  tourId: string | null | undefined;
  roundId: string | null | undefined;
  holeNumber: number;
  token: string | null | undefined;
};

// react-native-web's Alert.alert is a silent no-op, so web failures fall back to window.alert.
const notify = (title: string, message: string) => {
  if (Platform.OS === 'web') {
    window.alert(`${title}\n${message}`);
    return;
  }
  Alert.alert(title, message);
};

/**
 * Camera button for the hole screen header — the primary way photos get taken, because players are
 * standing on the hole when something worth photographing happens.
 *
 * Shoot-and-go by design: capture, dismiss, and the upload continues in the background with the
 * round and hole filled in from context. No caption prompt and no progress modal — nobody wants a
 * text field while holding a putter, and captions can be added later from the gallery.
 *
 * All upload state lives in the module-scoped queue, never in this component or its parent: the
 * hole screen unmounts the instant a player advances to the next hole, which is exactly when
 * someone who just took a photo is most likely to move on.
 *
 * On web the button drives a hidden `<input type="file" capture="environment">` instead of
 * expo-image-picker (a silent no-op in browsers — see pickPhotos): the camera on mobile browsers,
 * a file dialog on desktop.
 */
export default function HolePhotoButton({
  tourId,
  roundId,
  holeNumber,
  token,
}: HolePhotoButtonProps) {
  const { t } = useI18n();
  const [capturing, setCapturing] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const reportedErrorRef = useRef<string | null>(null);
  const webCameraInputRef = useRef<HTMLInputElement | null>(null);

  // A note rendered inside a header button is clipped to nothing, so problems have to be reported
  // out of band. An Alert is intrusive, but a photo that silently fails to upload is worse: the
  // player walks on believing it was saved. The ref keeps one failure to one alert — the queue holds
  // lastError until the next enqueue, so every later notification would otherwise re-fire it.
  useEffect(
    () =>
      subscribeToPhotoUploads((state) => {
        setPendingCount(state.pending.length);
        if (state.lastError && state.lastError !== reportedErrorRef.current) {
          reportedErrorRef.current = state.lastError;
          notify(t('tourPhotos.uploadFailedTitle'), t('tourPhotos.uploadFailed'));
        }
        if (!state.lastError) {
          reportedErrorRef.current = null;
        }
      }),
    [t],
  );

  useEffect(() => {
    setPhotoUploadAuthToken(token);
  }, [token]);

  const handleOutcome = useCallback(
    (outcome: PickOutcome) => {
      if (!tourId) {
        return;
      }
      if (outcome.kind === 'permission-denied') {
        notify(t('tourPhotos.uploadFailedTitle'), t('tourPhotos.cameraDenied'));
        return;
      }
      if (outcome.kind === 'rejected') {
        notify(
          t('tourPhotos.uploadFailedTitle'),
          outcome.reason === 'too-large' ? t('tourPhotos.tooLarge') : t('tourPhotos.unsupportedType'),
        );
        return;
      }
      if (outcome.kind === 'cancelled') {
        return;
      }

      outcome.photos.forEach((photo) => {
        enqueuePhotoUpload({
          id: newPhotoId(),
          tourId,
          // Tagged from context, which is the whole point of capturing here: no round picker, no
          // hole picker, no post-hoc data entry.
          roundId: roundId ?? null,
          holeNumber,
          caption: null,
          photo,
        });
      });
    },
    [holeNumber, roundId, t, tourId],
  );

  const capture = useCallback(async () => {
    if (!tourId || capturing) {
      return;
    }

    if (Platform.OS === 'web') {
      webCameraInputRef.current?.click();
      return;
    }

    setCapturing(true);
    try {
      handleOutcome(await pickFromCamera());
    } finally {
      setCapturing(false);
    }
  }, [capturing, handleOutcome, tourId]);

  const onWebFilesSelected = useCallback(
    (event: { target: HTMLInputElement }) => {
      const files = Array.from(event.target.files ?? []);
      // Reset so picking the same file twice in a row still fires a change event.
      event.target.value = '';
      if (files.length > 0) {
        handleOutcome(fromWebFiles(files));
      }
    },
    [handleOutcome],
  );

  // A round outside a tour has nowhere to file a photo.
  if (!tourId) {
    return null;
  }

  return (
    <Pressable
      onPress={() => void capture()}
      disabled={capturing}
      accessibilityRole="button"
      accessibilityLabel={t('tourPhotos.captureAction')}
      testID="hole-photo-capture"
      style={({ pressed }) => [styles.button, pressed && styles.buttonPressed]}
      hitSlop={8}
    >
      {capturing ? (
        <ActivityIndicator size="small" color={appColors.accent.primary} />
      ) : (
        <Text style={styles.icon}>{'▣'}</Text>
      )}
      {/* How many uploads are still in flight, so the player can see the photo is on its way rather
          than having to trust it. Failures come through as an Alert — see the subscription above. */}
      {pendingCount > 0 ? <Text style={styles.badge}>{pendingCount}</Text> : null}
      {/* Web only. `capture="environment"` opens the camera directly in mobile browsers; desktop
          falls back to a file dialog. Same pattern as PhotoPickerBar. */}
      {Platform.OS === 'web' ? (
        <input
          ref={webCameraInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          capture="environment"
          onChange={onWebFilesSelected as unknown as React.ChangeEventHandler<HTMLInputElement>}
          style={{ display: 'none' }}
          data-testid="hole-photo-camera-input"
        />
      ) : null}
    </Pressable>
  );
}

const styles = StyleSheet.create({
  button: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 10,
    paddingVertical: 4,
    marginRight: 4,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: alpha(appColors.accent.primary, 0.45),
    backgroundColor: alpha(appColors.surface.tintMid, 0.6),
  },
  buttonPressed: {
    backgroundColor: alpha(appColors.accent.primary, 0.2),
  },
  icon: {
    color: appColors.text.accentSoft,
    fontSize: 15,
    fontWeight: '700',
  },
  badge: {
    color: appColors.text.accentSoft,
    fontSize: 11,
    fontWeight: '700',
  },
});
